import { LegalLayout } from "@/components/LegalLayout";

const H2 = ({ children }) => (
  <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white mt-4">
    {children}
  </h2>
);

const H3 = ({ children }) => (
  <h3 className="text-base sm:text-lg font-semibold tracking-tight text-white">
    {children}
  </h3>
);

const P = ({ children }) => (
  <p className="text-base text-slate-400 leading-relaxed">{children}</p>
);

const UL = ({ items }) => (
  <ul className="flex flex-col gap-2 pl-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3 text-base text-slate-400 leading-relaxed">
        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#00DC82]" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Section = ({ children }) => (
  <section className="flex flex-col gap-4">{children}</section>
);

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="July 2026">
      <Section>
        <P>
          MockTP respects your privacy and is committed to protecting your personal
          information.
        </P>
        <P>
          This Privacy Policy explains how information is collected, used and protected
          when you use the MockTP website and the MockTP mobile application.
        </P>
        <P>By using MockTP, you agree to the practices described in this Privacy Policy.</P>
      </Section>

      <Section>
        <H2>Who We Are</H2>
        <P>MockTP is developed by MockTP Labs (trading name).</P>
        <P>
          Website:{" "}
          <a
            href="https://mocktp.app"
            className="text-[#00DC82] hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            mocktp.app
          </a>
        </P>
        <P>
          At the time of publication, MockTP Labs is not a registered company.
        </P>
      </Section>

      <Section>
        <H2>Who MockTP Is For</H2>
        <P>
          MockTP is designed for professional driving instructors aged 18 years or older.
        </P>
        <P>
          It is intended to assist instructors in carrying out mock driving assessments,
          monitoring student progress and producing professional assessment reports.
        </P>
        <P>MockTP is not intended for use by children.</P>
      </Section>

      <Section>
        <H2>Information We Collect</H2>

        <H3>Information You Provide Through the Website</H3>
        <P>
          If you contact us or register interest in MockTP, we may collect information
          that you choose to provide, including:
        </P>
        <UL
          items={[
            "Name",
            "Email address",
            "Country",
            "ADI number (if provided)",
            "Driving school or instructor name (if provided)",
            "Any information included in your message.",
          ]}
        />
        <P>
          This information is used only to respond to your enquiry and communicate with
          you about MockTP.
        </P>

        <H3>Information Stored Within the Mobile App</H3>
        <P>
          MockTP stores assessment information locally on the instructor's device.
        </P>
        <P>This may include:</P>
        <UL
          items={[
            "Student names",
            "Driver numbers",
            "Email addresses",
            "Telephone numbers",
            "Assessment history",
            "Instructor notes",
            "Assessment reports",
          ]}
        />
        <P>MockTP does not store this student information on its own servers.</P>
        <P>
          All student information remains under the control of the instructor using the
          app.
        </P>

        <H3>Exported Data</H3>
        <P>
          MockTP allows instructors to export their own data for backup or transfer
          purposes.
        </P>
        <P>
          Any exported files are created by the user and remain entirely under the user's
          control.
        </P>
        <P>Users are responsible for storing exported files securely.</P>
      </Section>

      <Section>
        <H2>How We Use Your Information</H2>
        <P>Information collected through the MockTP website may be used to:</P>
        <UL
          items={[
            "Respond to your enquiries.",
            "Communicate with people who register interest in MockTP.",
            "Communicate important information about MockTP.",
            "Improve the MockTP website and services.",
            "Comply with legal obligations where required.",
          ]}
        />
        <P>MockTP does not sell, rent or trade your personal information.</P>
      </Section>

      <Section>
        <H2>Cookies</H2>
        <P>
          At the time of publication, the MockTP website uses only cookies that are
          necessary for the website to function correctly.
        </P>
        <P>
          These essential cookies help provide a secure and reliable browsing experience.
        </P>
        <P>
          If additional cookies are introduced in the future, this Privacy Policy will be
          updated before they are used.
        </P>
      </Section>

      <Section>
        <H2>Analytics</H2>
        <P>
          At the time of publication, the MockTP website does not use website analytics
          services.
        </P>
        <P>
          If analytics are introduced in the future to help improve the website or
          services, this Privacy Policy will be updated before they are enabled.
        </P>
      </Section>

      <Section>
        <H2>Data Security</H2>
        <P>
          MockTP takes reasonable technical and organisational measures to protect the
          information submitted through the website.
        </P>
        <P>
          Although every effort is made to keep your information secure, no method of
          electronic storage or transmission over the internet can be guaranteed to be
          completely secure.
        </P>
        <P>For this reason, absolute security cannot be guaranteed.</P>
      </Section>

      <Section>
        <H2>Data Retention</H2>
        <P>
          Information submitted through the website is retained only for as long as
          reasonably necessary to:
        </P>
        <UL
          items={[
            "Respond to enquiries.",
            "Manage launch-interest enquiries.",
            "Improve MockTP.",
            "Meet legal obligations where applicable.",
          ]}
        />
        <P>
          When information is no longer required, it will be securely deleted or
          anonymised where appropriate.
        </P>
      </Section>

      <Section>
        <H2>Your Privacy Choices</H2>
        <P>You may contact MockTP at any time to:</P>
        <UL
          items={[
            "Request access to the personal information we hold about you.",
            "Request correction of inaccurate information.",
            "Request deletion of your personal information where applicable.",
            "Withdraw from launch-interest communications.",
          ]}
        />
        <P>
          Requests will be considered in accordance with applicable Irish and European
          data protection laws.
        </P>
      </Section>

      <Section>
        <H2>Children's Privacy</H2>
        <P>
          MockTP is intended for professional driving instructors aged 18 years or older.
        </P>
        <P>
          The website and mobile application are not intended for children, and MockTP
          does not knowingly collect personal information from children.
        </P>
        <P>
          If we become aware that personal information has been collected from a child, we
          will take reasonable steps to delete it.
        </P>
      </Section>

      <Section>
        <H2>Changes to This Privacy Policy</H2>
        <P>
          MockTP may update this Privacy Policy from time to time to reflect changes to
          the website, mobile application, services or legal requirements.
        </P>
        <P>The latest version will always be published on the MockTP website.</P>
        <P>We encourage users to review this Privacy Policy periodically.</P>
      </Section>

      <Section>
        <H2>Contact</H2>
        <P>
          If you have any questions about this Privacy Policy or how your information is
          handled, please contact:
        </P>
        <P>MockTP Labs</P>
        <P>
          Website:{" "}
          <a
            href="https://mocktp.app"
            className="text-[#00DC82] hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            mocktp.app
          </a>
        </P>
        <P>
          Email:{" "}
          <a href="mailto:privacy@mocktp.app" className="text-[#00DC82] hover:underline">
            privacy@mocktp.app
          </a>
        </P>
        <P>
          (This contact email may change in the future. The latest contact details will
          always be available on the MockTP website.)
        </P>
      </Section>

      <Section>
        <H2>Governing Law</H2>
        <P>This Privacy Policy is governed by the laws of Ireland.</P>
        <P>
          Where applicable, the processing of personal information is carried out in
          accordance with Irish and European data protection legislation.
        </P>
      </Section>

      <Section>
        <H2>Final Statement</H2>
        <P>
          MockTP is committed to protecting your privacy and handling your information
          responsibly.
        </P>
        <P>
          Our goal is to collect only the information necessary to provide and improve
          MockTP while keeping you informed about how your information is used.
        </P>
        <P>Thank you for trusting MockTP.</P>
      </Section>
    </LegalLayout>
  );
}
